/**
 * Third-party AI consent gate (U-7, D-7). Signed in and enforced (companion mode, or the web once
 * `AI_CONSENT_ENFORCED_ON_WEB` is on): fetch `GET /user/ai-consent`; while the current version isn't accepted
 * the consent sheet shows, and a decline leaves the app behind the "AI consent needed" screen until the user
 * reviews and accepts, or signs out. See src/native/aiConsent.ts for the state rules.
 */
import { computed, ref, watch } from 'vue'
import { getAiConsent, postAiConsent } from '@/api/aiConsent.api'
import { AI_CONSENT_ENFORCED_ON_WEB, type AiConsentStatus, aiConsentGateState } from '@/native/aiConsent'
import { isCompanionApp } from '@/native/companion'
import { onAppResume } from '@/native/lifecycle'
import { useAuthStore } from '@/stores/auth'
import { isImpersonatingClient } from '@/utils/impersonationHeaders'

const status = ref<AiConsentStatus | null>(null)
const unavailable = ref(false)
const reviewing = ref(false)
const saving = ref(false)
const error = ref('')
let loadedForUser: string | null = null
let started = false

export function aiConsentEnforced(): boolean {
    return isCompanionApp() || AI_CONSENT_ENFORCED_ON_WEB
}

async function load(userId: string): Promise<void> {
    const next = await getAiConsent().catch(() => null)
    if (loadedForUser !== userId) return
    status.value = next
    unavailable.value = !next
}

export default function useAiConsent() {
    const auth = useAuthStore()
    const userId = computed<string | null>(() => (auth.isAuthenticated ? String(auth.currentUser?.id ?? '') || null : null))

    const state = computed(() => aiConsentGateState({
        enforced: aiConsentEnforced(),
        signedIn: !!userId.value,
        impersonating: isImpersonatingClient(),
        status: status.value,
        unavailable: unavailable.value,
        reviewing: reviewing.value,
    }))

    function start(): void {
        if (started) return
        started = true
        watch(userId, (id) => {
            status.value = null
            unavailable.value = false
            reviewing.value = false
            error.value = ''
            loadedForUser = id
            if (id && aiConsentEnforced() && !isImpersonatingClient()) void load(id)
        }, { immediate: true })
        // A failed read (offline at launch) is retried when the app comes back.
        onAppResume(() => {
            const id = userId.value
            if (id && unavailable.value && aiConsentEnforced()) {
                loadedForUser = id
                void load(id)
            }
        })
    }

    async function respond(accepted: boolean): Promise<boolean> {
        const current = status.value
        if (!current || saving.value) return false
        saving.value = true
        error.value = ''
        try {
            const next = await postAiConsent(current.current_version, accepted).catch(() => null)
            if (!next) {
                error.value = 'Something went wrong. Please try again.'
                return false
            }
            // A decline always lands on the blocking screen, even if the response omits the timestamp.
            status.value = !accepted && next.required && !next.declined_at
                ? { ...next, declined_at: new Date().toISOString() }
                : next
            reviewing.value = false
            return true
        } finally {
            saving.value = false
        }
    }

    return {
        state,
        status,
        saving,
        error,
        start,
        accept: () => respond(true),
        decline: () => respond(false),
        review: () => { reviewing.value = true },
    }
}
